import type { ComponentProps } from 'react';
import { CalendarIcon } from 'lucide-react';
import { Button, Calendar, Label, Popover, PopoverContent, PopoverTrigger } from 'shared-ui';

function isoToDate(iso: string): Date {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d); // local time — avoids UTC/timezone drift
}

function dateToIso(date: Date): string {
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${String(date.getFullYear())}-${m}-${d}`;
}

function formatDisplayDate(iso: string): string {
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
}

export interface DateFieldProps {
    id: string;
    label: string;
    placeholder: string;
    /** ISO YYYY-MM-DD, or null when unset. */
    value: string | null;
    onChange: (v: string | null) => void;
    /** Preferred side for the calendar popover. It still flips if there isn't room. Defaults to bottom. */
    side?: ComponentProps<typeof PopoverContent>['side'];
}

/**
 * Labelled date picker: a visual Calendar popover plus an sr-only native date input that
 * carries the id (so the Label association resolves to a real form control, enabling
 * fireEvent.change in tests and a native control for screen readers).
 */
export function DateField({ id, label, placeholder, value, onChange, side = 'bottom' }: DateFieldProps) {
    const selectedDate = value ? isoToDate(value) : undefined;

    return (
        <div className="space-y-1.5">
            <Label htmlFor={id} className="text-foreground text-sm leading-5 font-medium">
                {label}
            </Label>
            <input
                id={id}
                type="date"
                className="sr-only"
                tabIndex={-1}
                value={value ?? ''}
                onChange={(e) => {
                    onChange(e.target.value || null);
                }}
            />
            <Popover>
                <PopoverTrigger
                    render={
                        <Button
                            variant="outline"
                            className="text-muted-foreground dark:border-border dark:bg-background dark:hover:bg-muted h-10 w-full justify-between text-left text-sm leading-5 font-normal tracking-normal"
                        >
                            {value ? formatDisplayDate(value) : placeholder}
                            <CalendarIcon className="text-origin-blue ml-2 size-4 shrink-0" />
                        </Button>
                    }
                />
                <PopoverContent className="w-auto p-0" align="start" side={side}>
                    <Calendar
                        mode="single"
                        fixedWeeks
                        selected={selectedDate}
                        onSelect={(date) => {
                            onChange(date ? dateToIso(date) : null);
                        }}
                        className="[--cell-size:--spacing(9)]"
                    />
                </PopoverContent>
            </Popover>
        </div>
    );
}
